import { UserAgent, Registerer, RegistererState, Inviter, Invitation, SessionState } from 'sip.js';

// src/sip/SipService.ts

// src/constants/sip.ts
var SIP_DEFAULTS = {
  WSS_PORT: 8089,
  REGISTER_EXPIRES: 300,
  KEEPALIVE_INTERVAL: 3e4,
  RECONNECT_MIN: 1e3,
  RECONNECT_MAX: 6e4
};
var EXTENSION_RANGES = {
  VIDEOPHONE: { min: 7e3, max: 7999 },
  EMERGENCY: { min: 9e3, max: 9002 },
  ENTRANCE_DOOR: { min: 9100, max: 9199 }
};
var isEmergencyExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.EMERGENCY.min && num <= EXTENSION_RANGES.EMERGENCY.max;
};
var isVideophoneExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.VIDEOPHONE.min && num <= EXTENSION_RANGES.VIDEOPHONE.max;
};
var isEntranceDoorExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.ENTRANCE_DOOR.min && num <= EXTENSION_RANGES.ENTRANCE_DOOR.max;
};

// src/sip/SipService.ts
var SipService = class {
  config = null;
  userAgent = null;
  registerer = null;
  state = "disconnected";
  handlers = /* @__PURE__ */ new Map();
  keepAliveTimer = null;
  reconnectTimer = null;
  reconnectDelay = SIP_DEFAULTS.RECONNECT_MIN;
  _cleaningUp = false;
  async connect(config) {
    this.clearReconnectTimer();
    this._cleaningUp = false;
    this.config = config;
    this.setState("connecting");
    try {
      const transportOptions = {
        server: config.wssUrl,
        keepAliveInterval: SIP_DEFAULTS.KEEPALIVE_INTERVAL / 1e3
      };
      const uri = UserAgent.makeURI(`sip:${config.extension}@${config.realm}`);
      if (!uri) throw new Error(`Invalid SIP URI: sip:${config.extension}@${config.realm}`);
      const userAgentOptions = {
        uri,
        authorizationUsername: config.extension,
        authorizationPassword: config.password,
        displayName: config.displayName,
        transportOptions,
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: config.iceServers.map((s) => ({
              urls: s.urls,
              username: s.username,
              credential: s.credential
            }))
          }
        },
        logLevel: "warn"
      };
      this.userAgent = new UserAgent(userAgentOptions);
      this.userAgent.delegate = {
        onInvite: (invitation) => {
          this.emit("incomingCall", invitation);
        }
      };
      await this.userAgent.start();
      try {
        const transport = this.userAgent.transport;
        const ws = transport?._ws || transport?.ws;
        if (ws) {
          const origOnMessage = ws.onmessage;
          ws.onmessage = (event) => {
            if (typeof event.data === "string" && event.data.length > 4) {
              console.log("[SIP RAW \u2190]", event.data.substring(0, 500));
            }
            origOnMessage?.call(ws, event);
          };
          console.log("[SipService] Raw WS message interceptor installed");
        }
      } catch {
      }
      this.registerer = new Registerer(this.userAgent, {
        expires: SIP_DEFAULTS.REGISTER_EXPIRES
      });
      this.registerer.stateChange.addListener((newState) => {
        switch (newState) {
          case RegistererState.Registered:
            this.setState("registered");
            this.reconnectDelay = SIP_DEFAULTS.RECONNECT_MIN;
            break;
          case RegistererState.Unregistered:
            this.setState("disconnected");
            if (!this._cleaningUp) {
              console.warn("[SipService] Registration lost \u2014 scheduling reconnect");
              this.emit("registrationFailed", new Error("Registration rejected"));
              this.scheduleReconnect();
            }
            break;
          case RegistererState.Terminated:
            this.setState("disconnected");
            break;
        }
      });
      await this.registerer.register();
      this.startKeepAlive();
    } catch (err) {
      console.error("[SipService] Connection error:", err);
      this.setState("error");
      this.emit("transportError", err);
      this.scheduleReconnect();
    }
  }
  async disconnect() {
    this.stopKeepAlive();
    this.clearReconnectTimer();
    this._cleaningUp = true;
    if (this.registerer) {
      try {
        await this.registerer.unregister();
      } catch {
      }
      this.registerer = null;
    }
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
      }
      this.userAgent = null;
    }
    this.config = null;
    this._cleaningUp = false;
    this.setState("disconnected");
  }
  /**
   * Create an outgoing call (Inviter).
   */
  makeCall(target) {
    if (!this.userAgent || this.state !== "registered") {
      console.warn("[SipService] Cannot make call \u2014 not registered");
      return null;
    }
    const targetUri = UserAgent.makeURI(`sip:${target}@${this.config.realm}`);
    if (!targetUri) {
      console.error(`[SipService] Invalid target URI: ${target}`);
      return null;
    }
    const inviter = new Inviter(this.userAgent, targetUri, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
        iceGatheringTimeout: 5
      }
    });
    return inviter;
  }
  /**
   * Answer an incoming call (Invitation).
   */
  async answerCall(invitation) {
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
        iceGatheringTimeout: 5
      }
    });
  }
  /**
   * Reject an incoming call.
   */
  async rejectCall(invitation) {
    await invitation.reject();
  }
  isRegistered() {
    return this.state === "registered";
  }
  getState() {
    return this.state;
  }
  getConfig() {
    return this.config;
  }
  getUserAgent() {
    return this.userAgent;
  }
  getKeepAliveInterval() {
    return SIP_DEFAULTS.KEEPALIVE_INTERVAL;
  }
  /**
   * Tear down UA/registerer without clearing config or reconnect state.
   * Used internally before reconnect attempts.
   */
  async cleanup() {
    this.stopKeepAlive();
    this._cleaningUp = true;
    if (this.registerer) {
      try {
        await this.registerer.unregister();
      } catch {
      }
      this.registerer = null;
    }
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
      }
      this.userAgent = null;
    }
    this._cleaningUp = false;
  }
  // --- Event system ---
  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, /* @__PURE__ */ new Set());
    }
    this.handlers.get(event).add(handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }
  emit(event, data) {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.error(`[SipService] Event handler error (${event}):`, err);
      }
    });
  }
  // --- Internal ---
  setState(newState) {
    if (this.state !== newState) {
      this.state = newState;
      this.emit("stateChange", newState);
    }
  }
  startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (this.userAgent && this.state === "registered") ;
    }, SIP_DEFAULTS.KEEPALIVE_INTERVAL);
  }
  stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }
  scheduleReconnect() {
    this.clearReconnectTimer();
    const jitter = Math.random() * 1e3;
    const delay = Math.min(this.reconnectDelay + jitter, SIP_DEFAULTS.RECONNECT_MAX);
    console.log(`[SipService] Reconnecting in ${Math.round(delay)}ms`);
    this.reconnectTimer = setTimeout(async () => {
      const savedConfig = this.config;
      if (savedConfig) {
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, SIP_DEFAULTS.RECONNECT_MAX);
        await this.cleanup();
        await this.connect(savedConfig);
      }
    }, delay);
  }
  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
};
var CallManager = class {
  sipService;
  currentCall = null;
  currentSession = null;
  handlers = /* @__PURE__ */ new Map();
  constructor(sipService) {
    this.sipService = sipService;
  }
  /**
   * Initiate an outgoing call.
   */
  async initiate(target, type) {
    if (this.currentCall && this.currentCall.state !== "ended") {
      console.warn("[CallManager] Already in a call");
      return null;
    }
    const callType = type || this.inferCallType(target);
    const inviter = this.sipService.makeCall(target);
    if (!inviter) return null;
    const call = {
      id: crypto.randomUUID(),
      state: "dialing",
      type: callType,
      direction: "outbound",
      remoteParty: target,
      startedAt: (/* @__PURE__ */ new Date()).toISOString(),
      isEmergency: isEmergencyExtension(target),
      isAutoAnswer: false,
      isMuted: false,
      isSpeaker: false
    };
    this.currentCall = call;
    this.currentSession = inviter;
    this.emit("callStateChange", call);
    this.attachSessionListeners(inviter);
    try {
      await inviter.invite();
    } catch (err) {
      console.error("[CallManager] Invite failed:", err);
      this.updateCallState("ended");
      return null;
    }
    return call;
  }
  /**
   * Handle an incoming call from SipService.
   */
  handleIncoming(invitation) {
    const remoteUri = invitation.remoteIdentity.uri.toString();
    const remoteExt = invitation.remoteIdentity.uri.user || remoteUri;
    const displayName = invitation.remoteIdentity.displayName || remoteExt;
    const call = {
      id: crypto.randomUUID(),
      state: "ringing_in",
      type: this.inferCallType(remoteExt),
      direction: "inbound",
      remoteParty: remoteExt,
      remoteDisplayName: displayName,
      startedAt: (/* @__PURE__ */ new Date()).toISOString(),
      isEmergency: isEmergencyExtension(remoteExt),
      isAutoAnswer: this.shouldAutoAnswer(
        remoteExt,
        this.extractSipHeaders(invitation)
      ),
      isMuted: false,
      isSpeaker: false
    };
    this.currentCall = call;
    this.currentSession = invitation;
    this.attachSessionListeners(invitation);
    this.emit("callStateChange", call);
    if (call.isAutoAnswer) {
      setTimeout(() => this.answer(), 500);
    }
    return call;
  }
  /**
   * Answer the current incoming call.
   */
  async answer() {
    if (!this.currentSession || !(this.currentSession instanceof Invitation)) {
      console.warn("[CallManager] No incoming call to answer");
      return;
    }
    await this.sipService.answerCall(this.currentSession);
  }
  /**
   * Reject the current incoming call.
   */
  async reject() {
    if (!this.currentSession || !(this.currentSession instanceof Invitation)) {
      console.warn("[CallManager] No incoming call to reject");
      return;
    }
    await this.sipService.rejectCall(this.currentSession);
    this.updateCallState("ended");
  }
  /**
   * Put the current call on hold.
   */
  async hold() {
    if (!this.currentSession || this.currentCall?.state !== "active") return;
    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler && "hold" in sessionDescriptionHandler) {
        await sessionDescriptionHandler.hold();
        this.updateCallState("held");
      }
    } catch (err) {
      console.error("[CallManager] Hold failed:", err);
    }
  }
  /**
   * Resume a held call.
   */
  async unhold() {
    if (!this.currentSession || this.currentCall?.state !== "held") return;
    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler;
      if (sessionDescriptionHandler && "unhold" in sessionDescriptionHandler) {
        await sessionDescriptionHandler.unhold();
        this.updateCallState("active");
      }
    } catch (err) {
      console.error("[CallManager] Unhold failed:", err);
    }
  }
  /**
   * End the active call.
   */
  async hangup() {
    if (!this.currentSession) return;
    try {
      switch (this.currentSession.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (this.currentSession instanceof Inviter) {
            await this.currentSession.cancel();
          } else if (this.currentSession instanceof Invitation) {
            await this.currentSession.reject();
          }
          break;
        case SessionState.Established:
          await this.currentSession.bye();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("[CallManager] Hangup error:", err);
    }
    this.updateCallState("ended");
  }
  /**
   * Send DTMF tones (e.g. for door open codes).
   * Uses RFC 4733 (RTCDTMFSender) for WebRTC, falls back to SIP INFO.
   */
  async sendDtmf(digits) {
    if (!this.currentSession || this.currentCall?.state !== "active") {
      console.warn("[CallManager] Cannot send DTMF \u2014 no active call");
      return;
    }
    try {
      const pc = this.getPeerConnection();
      const audioSender = pc?.getSenders().find((s) => s.track?.kind === "audio");
      if (audioSender?.dtmf) {
        console.log("[CallManager] Sending DTMF via RFC 4733:", digits);
        audioSender.dtmf.insertDTMF(digits, 160, 80);
        this.emit("dtmfSent", digits);
        return;
      }
      console.log("[CallManager] Sending DTMF via SIP INFO:", digits);
      for (const digit of digits) {
        const body = {
          contentDisposition: "render",
          contentType: "application/dtmf-relay",
          content: `Signal=${digit}\r
Duration=160`
        };
        await this.currentSession.info({ requestOptions: { body } });
        await new Promise((r) => setTimeout(r, 200));
      }
      this.emit("dtmfSent", digits);
    } catch (err) {
      console.error("[CallManager] DTMF failed:", err);
    }
  }
  /**
   * Toggle mute on the current call.
   */
  toggleMute() {
    if (!this.currentCall || !this.currentSession) return;
    const pc = this.getPeerConnection();
    if (!pc) return;
    const audioSenders = pc.getSenders().filter((s) => s.track?.kind === "audio");
    const muted = !this.currentCall.isMuted;
    audioSenders.forEach((sender) => {
      if (sender.track) sender.track.enabled = !muted;
    });
    this.currentCall = { ...this.currentCall, isMuted: muted };
    this.emit("callStateChange", this.currentCall);
  }
  /**
   * Toggle speaker (for native bridge integration).
   */
  toggleSpeaker() {
    if (!this.currentCall) return;
    this.currentCall = { ...this.currentCall, isSpeaker: !this.currentCall.isSpeaker };
    this.emit("callStateChange", this.currentCall);
  }
  getCurrentCall() {
    return this.currentCall;
  }
  /**
   * Get the underlying RTCPeerConnection (for stats/media access).
   */
  getPeerConnection() {
    if (!this.currentSession) return null;
    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!sdh) return null;
    try {
      if (sdh.peerConnection) return sdh.peerConnection;
    } catch {
    }
    return sdh._peerConnection || null;
  }
  /**
   * Get the remote MediaStream managed by sip.js's SessionDescriptionHandler.
   * This is the most reliable way to get remote audio.
   */
  getRemoteMediaStream() {
    if (!this.currentSession) return null;
    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!sdh) return null;
    return sdh.remoteMediaStream || sdh._remoteMediaStream || null;
  }
  /**
   * Determine if an incoming call should be auto-answered.
   */
  shouldAutoAnswer(callerExtension, headers) {
    if (isEmergencyExtension(callerExtension)) return true;
    if (headers?.["Alert-Info"]?.includes("auto-answer")) return true;
    return false;
  }
  // --- Event system ---
  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, /* @__PURE__ */ new Set());
    }
    this.handlers.get(event).add(handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }
  emit(event, data) {
    this.handlers.get(event)?.forEach((h) => {
      try {
        h(data);
      } catch (err) {
        console.error(`[CallManager] Event handler error (${event}):`, err);
      }
    });
  }
  // --- Internal ---
  attachSessionListeners(session) {
    session.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Establishing:
          this.updateCallState("connecting");
          break;
        case SessionState.Established:
          if (this.currentCall) {
            this.currentCall = {
              ...this.currentCall,
              connectedAt: (/* @__PURE__ */ new Date()).toISOString()
            };
          }
          this.updateCallState("active");
          break;
        case SessionState.Terminated:
          this.updateCallState("ended");
          break;
      }
    });
  }
  updateCallState(state) {
    if (!this.currentCall) return;
    const updatedCall = {
      ...this.currentCall,
      state
    };
    if (state === "ended") {
      updatedCall.endedAt = (/* @__PURE__ */ new Date()).toISOString();
      if (updatedCall.connectedAt) {
        updatedCall.duration = Math.round(
          (new Date(updatedCall.endedAt).getTime() - new Date(updatedCall.connectedAt).getTime()) / 1e3
        );
      }
    }
    this.currentCall = updatedCall;
    this.emit("callStateChange", updatedCall);
    if (state === "ended") {
      this.emit("callEnded", updatedCall);
      setTimeout(() => {
        this.currentCall = null;
        this.currentSession = null;
      }, 2e3);
    }
  }
  inferCallType(extension) {
    if (isEmergencyExtension(extension)) return "emergency";
    if (isVideophoneExtension(extension)) return "doorbell";
    if (isEntranceDoorExtension(extension)) return "intercom";
    return "voice";
  }
  extractSipHeaders(invitation) {
    const headers = {};
    try {
      const msg = invitation.request;
      const alertInfo = msg.getHeader("Alert-Info");
      if (alertInfo) headers["Alert-Info"] = alertInfo;
      const callInfo = msg.getHeader("Call-Info");
      if (callInfo) headers["Call-Info"] = callInfo;
    } catch {
    }
    return headers;
  }
};

// src/sip/WebRTCMedia.ts
var WebRTCMedia = class {
  localStream = null;
  remoteAudioElement = null;
  /**
   * Get local audio stream for calls.
   */
  async getLocalAudioStream() {
    if (this.localStream) return this.localStream;
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: false
    });
    return this.localStream;
  }
  /**
   * Pre-create the audio element during a user gesture (tap Call / Answer).
   * This "warms up" the audio context so play() succeeds later when remote
   * media arrives, even if the actual play call is in a state-change callback.
   */
  warmUp() {
    const el = this.ensureAudioElement();
    el.srcObject = null;
    el.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    el.volume = 1;
    el.play().then(() => {
      el.pause();
      el.src = "";
      console.log("[WebRTCMedia] Audio element warmed up (autoplay unlocked)");
    }).catch(() => {
      console.log("[WebRTCMedia] Warm-up play ignored (will retry on stream attach)");
    });
  }
  ensureAudioElement() {
    if (!this.remoteAudioElement) {
      this.remoteAudioElement = document.createElement("audio");
      this.remoteAudioElement.autoplay = true;
      this.remoteAudioElement.volume = 1;
      this.remoteAudioElement.setAttribute("playsinline", "");
      this.remoteAudioElement.style.position = "fixed";
      this.remoteAudioElement.style.top = "-1px";
      this.remoteAudioElement.style.left = "-1px";
      this.remoteAudioElement.style.width = "1px";
      this.remoteAudioElement.style.height = "1px";
      this.remoteAudioElement.style.opacity = "0";
      this.remoteAudioElement.style.pointerEvents = "none";
      document.body.appendChild(this.remoteAudioElement);
    }
    return this.remoteAudioElement;
  }
  /**
   * Play a remote MediaStream (preferred — use sip.js's remoteMediaStream).
   */
  playRemoteStream(stream) {
    const el = this.ensureAudioElement();
    el.srcObject = stream;
    el.volume = 1;
    const attemptPlay = (attempt) => {
      el.play().then(() => {
        console.log(`[WebRTCMedia] play() succeeded (attempt ${attempt})`);
      }).catch((err) => {
        console.warn(`[WebRTCMedia] play() blocked (attempt ${attempt}):`, err.message);
        if (attempt < 5) {
          setTimeout(() => attemptPlay(attempt + 1), 500);
        }
      });
    };
    attemptPlay(1);
    console.log(
      "[WebRTCMedia] Playing remote stream, tracks:",
      stream.getAudioTracks().map((t) => `${t.kind}:${t.id}(${t.readyState})`).join(", ") || "none"
    );
  }
  /**
   * Attach remote audio from a PeerConnection (fallback if remoteMediaStream unavailable).
   */
  attachRemoteAudio(pc) {
    const el = this.ensureAudioElement();
    const remoteStream = new MediaStream();
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });
    pc.addEventListener("track", (event) => {
      console.log("[WebRTCMedia] ontrack:", event.track.kind, event.track.id);
      const existing = remoteStream.getTracks().find((t) => t.id === event.track.id);
      if (!existing) {
        remoteStream.addTrack(event.track);
      }
      el.srcObject = remoteStream;
      el.volume = 1;
      el.play().catch(
        (err) => console.warn("[WebRTCMedia] play() blocked:", err.message)
      );
    });
    el.srcObject = remoteStream;
    el.volume = 1;
    el.play().catch(
      (err) => console.warn("[WebRTCMedia] Initial play() blocked:", err.message)
    );
    console.log(
      "[WebRTCMedia] Attached via PeerConnection, receivers:",
      pc.getReceivers().length,
      "tracks:",
      remoteStream.getTracks().length
    );
  }
  /**
   * Stop all local media tracks.
   */
  stopLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
  }
  /**
   * Clean up all media.
   */
  cleanup() {
    this.stopLocalStream();
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      this.remoteAudioElement.remove();
      this.remoteAudioElement = null;
    }
  }
  /**
   * Get WebRTC stats from a PeerConnection.
   */
  async getCallStats(pc) {
    const stats = {};
    try {
      const report = await pc.getStats();
      report.forEach((stat) => {
        if (stat.type === "candidate-pair" && stat.state === "succeeded") {
          stats.rtt = stat.currentRoundTripTime ? stat.currentRoundTripTime * 1e3 : void 0;
        }
        if (stat.type === "inbound-rtp" && stat.kind === "audio") {
          stats.jitter = stat.jitter ? stat.jitter * 1e3 : void 0;
          stats.packetLoss = stat.packetsLost;
        }
        if (stat.type === "codec" && stat.mimeType?.startsWith("audio/")) {
          stats.codec = stat.mimeType;
        }
      });
    } catch {
    }
    return stats;
  }
};

// src/sip/CallTones.ts
var sharedContext = null;
function getSharedContext() {
  if (!sharedContext) {
    sharedContext = new AudioContext();
  }
  if (sharedContext.state === "suspended") {
    sharedContext.resume().catch(() => {
    });
  }
  return sharedContext;
}
if (typeof document !== "undefined") {
  const unlock = () => {
    if (!sharedContext) {
      sharedContext = new AudioContext();
    }
    if (sharedContext.state === "suspended") {
      sharedContext.resume().catch(() => {
      });
    }
  };
  document.addEventListener("click", unlock, { capture: true, passive: true });
  document.addEventListener("touchstart", unlock, { capture: true, passive: true });
  document.addEventListener("keydown", unlock, { capture: true, passive: true });
}
var CallTones = class {
  intervalId = null;
  activeNodes = [];
  /**
   * Play a ringback tone (what the caller hears while waiting).
   * Standard North American ringback: 440Hz + 480Hz, 2s on / 4s off
   */
  startRingback() {
    this.stop();
    const ctx = getSharedContext();
    if (ctx.state === "suspended") return;
    const playBurst = () => {
      try {
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2);
        gain.connect(ctx.destination);
        const osc1 = ctx.createOscillator();
        osc1.frequency.value = 440;
        osc1.type = "sine";
        osc1.connect(gain);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 2);
        const osc2 = ctx.createOscillator();
        osc2.frequency.value = 480;
        osc2.type = "sine";
        osc2.connect(gain);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 2);
        this.activeNodes.push(osc1, osc2, gain);
      } catch {
      }
    };
    playBurst();
    this.intervalId = setInterval(playBurst, 6e3);
  }
  /**
   * Play a ringtone (what the callee hears on incoming call).
   * European-style double-ring: two short bursts per cycle.
   */
  startRingtone() {
    this.stop();
    const ctx = getSharedContext();
    if (ctx.state === "suspended") return;
    const playRing = () => {
      try {
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.setValueAtTime(0, ctx.currentTime + 0.4);
        gain.gain.setValueAtTime(0.2, ctx.currentTime + 0.6);
        gain.gain.setValueAtTime(0, ctx.currentTime + 1);
        const osc = ctx.createOscillator();
        osc.frequency.value = 440;
        osc.type = "sine";
        osc.connect(gain);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 1);
        this.activeNodes.push(osc, gain);
      } catch {
      }
    };
    playRing();
    this.intervalId = setInterval(playRing, 3e3);
  }
  /**
   * Stop all tones.
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.activeNodes.forEach((node) => {
      try {
        if (node instanceof OscillatorNode) node.stop();
        node.disconnect();
      } catch {
      }
    });
    this.activeNodes = [];
  }
};

export { CallManager, CallTones, SipService, WebRTCMedia };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map